import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EstadoVagaEnum,
} from '@app/features-modules/bolsa-de-profissionais/api';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { Avatar } from 'primeng/avatar';
import { AvatarGroup } from 'primeng/avatargroup';
import { ApiService } from '@core/providers/http';
import { getInitials } from '@app/shared/pipes/initials.pipe';
import {
  VagaService,
  CandidaturaService,
  EmpresaSectorService,
  VagaCompetenciaService,
} from '../../../services';
import { labelTipoAnexoProfissional } from '@app/features-modules/bolsa-de-profissionais/api/model/anexo-candidatura-custom.model';

@Component({
  selector: 'app-mais-detalhe-vaga',
  imports: [
    CommonModule,
    TagModule,
    Button,
    ConfirmDialog,
    Avatar,
    AvatarGroup,
  ],
  providers: [
    ConfirmationService,
    ApiService,
    MessageService,
    CandidaturaService,
    VagaService,
    VagaCompetenciaService,
    EmpresaSectorService,
  ],
  templateUrl: './mais-detalhe-vaga.component.html',
  styleUrl: './mais-detalhe-vaga.component.scss',
})
export class MaisDetalheVagaComponent implements OnInit {
  router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  vagaDetailID = -1;

  @Output() onCloseModal = new EventEmitter<boolean>();
  empresaLogoUrl: string | null = null;
  imageError = false;
  getInitials = getInitials;

  vagaSelected = input<any>();

  vagaCompetenciaService = inject(VagaCompetenciaService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  candidaturaService = inject(CandidaturaService);
  vagaService = inject(VagaService);
  empresaSectorService = inject(EmpresaSectorService);

  vagaDetail: any;
  isLoading: boolean = false;
  isLoadingCompetencias: boolean = false;
  listaCompetencias: any[] = [];
  candidatosCount: number | null = null;

  estadoOption = [
    { id: EstadoVagaEnum.NUMBER_1, designacao: 'Aberto' },
    { id: EstadoVagaEnum.NUMBER_2, designacao: 'Fechado' },
    { id: EstadoVagaEnum.NUMBER_3, designacao: 'Cancelado' },
  ];

  estadoVagaMap: Record<number, { label: string; severity: any }> = {
    1: { label: 'Aberto', severity: 'success' },
    2: { label: 'Fechado', severity: 'danger' },
    3: { label: 'Cancelado', severity: 'danger' },
  };

  nivelHabilitacaoMap: Record<number, string> = {
    1: 'Ensino Primário',
    2: '1º Ciclo do Secundário',
    3: 'Ensino Médio - 2º Ciclo do Secundário',
    4: 'Bacharelato',
    5: 'Licenciatura',
    6: 'Pós-Graduação',
    7: 'Mestrado',
    8: 'Doutoramento',
    9: 'Pós-Doutoramento',
  };

  anoExperienciaMap: Record<number, string> = {
    1: 'Sem experiência',
    2: '1 a 3 anos de experiência',
    3: '1 a 3 anos de experiência',
    4: '3 a 5 anos de experiência',
    5: '5 a 8 anos de experiência',
    6: 'Mais de 10 anos de experiência',
    7: 'Mais de 15 anos de experiência',
  };

  regimeTrabalhoMap: Record<number, string> = {
    1: 'Normal',
    2: 'Rotação Onshore',
    3: 'Rotação Offshore',
    4: 'Rotação Mista',
  };

  tipoContratoMap: Record<number, string> = {
    1: 'Contrato a Termo Certo',
    2: 'Contrato a Termo Incerto',
    3: 'Prestação de Serviço',
    4: 'Trabalho Temporário',
    5: 'Estágio Remunerado',
    6: 'Estágio Não Remunerado',
  };

  constructor() {
    effect(() => {
      const selected = this.vagaSelected();
      if (selected !== undefined && selected !== null) {
        this.initVagaData(selected);
      }
    });
  }

  ngOnInit(): void {
    if (!this.vagaDetail) {
      this.initVagaData(this.vagaSelected());
    }
  }

  private resolveVagaId(selected?: any): number | null {
    if (typeof selected === 'number' && !isNaN(selected) && selected > 0) {
      return selected;
    }
    if (typeof selected === 'string' && !isNaN(Number(selected)) && Number(selected) > 0) {
      return Number(selected);
    }
    if (selected && typeof selected === 'object' && selected.id && !isNaN(Number(selected.id))) {
      return Number(selected.id);
    }
    const routeParam = this.route?.snapshot?.params?.['id'] || this.route?.snapshot?.paramMap?.get('id');
    if (routeParam && !isNaN(Number(routeParam)) && Number(routeParam) > 0) {
      return Number(routeParam);
    }
    return null;
  }

  initVagaData(selected?: any): void {
    const vagaId = this.resolveVagaId(selected);

    if (selected && typeof selected === 'object' && selected.id) {
      this.vagaDetail = selected;
      if (Array.isArray(selected.vagaCompetencias) && selected.vagaCompetencias.length > 0) {
        this.listaCompetencias = selected.vagaCompetencias;
      }
      if (selected.empresaID) {
        this.loadEmpresaLogo(selected.empresaID);
      }
    }

    if (vagaId) {
      this.vagaDetailID = vagaId;
      this.loadVagaDetails(vagaId);
      this.loadCompetencias(vagaId);
      this.loadCandidaturas(vagaId);
    }
  }

  loadVagaDetails(vagaId: number): void {
    this.isLoading = true;
    this.vagaService.show(vagaId.toString()).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response?.retorno?.codigo === 200 && response.data) {
          this.vagaDetail = response.data;
          if (
            (!this.listaCompetencias || this.listaCompetencias.length === 0) &&
            Array.isArray(response.data.vagaCompetencias) &&
            response.data.vagaCompetencias.length > 0
          ) {
            this.listaCompetencias = response.data.vagaCompetencias;
          }
          if (this.vagaDetail?.empresaID) {
            this.loadEmpresaLogo(this.vagaDetail.empresaID);
          }
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Erro ao obter detalhes da vaga:', error);
      },
    });
  }

  loadCompetencias(vagaId: number): void {
    this.isLoadingCompetencias = true;
    this.vagaCompetenciaService
      .list({
        VagaID: vagaId,
        PageSize: 100,
      })
      .subscribe({
        next: (result: any) => {
          this.isLoadingCompetencias = false;
          if (result?.retorno?.codigo === 200 && Array.isArray(result?.data)) {
            this.listaCompetencias = result.data;
          } else if (Array.isArray(result?.data)) {
            this.listaCompetencias = result.data;
          } else if (Array.isArray(result)) {
            this.listaCompetencias = result;
          } else if (
            (!this.listaCompetencias || this.listaCompetencias.length === 0) &&
            Array.isArray(this.vagaDetail?.vagaCompetencias)
          ) {
            this.listaCompetencias = this.vagaDetail.vagaCompetencias;
          }
        },
        error: (error: any) => {
          this.isLoadingCompetencias = false;
          console.error('Erro ao carregar competências da vaga:', error);
          if (
            (!this.listaCompetencias || this.listaCompetencias.length === 0) &&
            Array.isArray(this.vagaDetail?.vagaCompetencias)
          ) {
            this.listaCompetencias = this.vagaDetail.vagaCompetencias;
          }
        },
      });
  }

  loadCandidaturas(vagaId: number): void {
    this.candidaturaService.list({ VagaID: vagaId }).subscribe({
      next: (result: any) => {
        if (result?.records !== undefined) {
          this.candidatosCount = result.records;
        } else if (Array.isArray(result?.data)) {
          this.candidatosCount = result.data.length;
        } else {
          this.candidatosCount = null;
        }
      },
      error: (err: any) => {
        console.error('Erro ao carregar candidaturas da vaga:', err);
      },
    });
  }

  loadEmpresaLogo(empresaId: number): void {
    this.empresaSectorService.getPhotoEmpresaIdGet(empresaId).subscribe({
      next: (blob: Blob) => {
        if (blob && blob.size > 0) {
          this.empresaLogoUrl = URL.createObjectURL(blob);
        }
      },
      error: (error) => {
        console.error('Erro ao obter foto da empresa:', error);
        this.empresaLogoUrl = null;
      },
    });
  }

  getCompetenciaNome(item: any): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item.competencia === 'string') return item.competencia;
    if (item.competencia && typeof item.competencia === 'object') {
      return item.competencia.designacao || item.competencia.nome || item.competencia.descricao || '';
    }
    return item.designacao || item.nome || item.descricao || '';
  }

  closeModal(): void {
    this.onCloseModal.emit(true);
  }

  trackById(index: number, item: any): any {
    return item?.id ?? index;
  }

  get documentosExigidos(): { id?: number; rotulo: string }[] {
    const tipos = this.vagaDetail?.vagaTipoAnexoCandidaturas || this.vagaDetail?.vagaTipoAnexoCandidatura || [];
    if (!Array.isArray(tipos)) return [];
    return tipos.map((t: any) => ({
      id: t.id,
      rotulo: t.tipoAnexoCandidatura || labelTipoAnexoProfissional(t.tipoAnexoCandidaturaEnum) || `Tipo ${t.tipoAnexoCandidaturaEnum}`
    }));
  }
}
